/**
 * Active runtime — which runtime is the user currently *monitoring*?
 *
 * This is purely a UI scoping concept. The backend tracks every runtime
 * independently; the user picks one in the Topbar, and the Dashboard +
 * Monitor pages filter their queries by `runtimeId`. Persisted to
 * localStorage so the choice survives reloads.
 *
 *   - On first load, picks the default runtime (the one with isDefault).
 *   - Selection persists to `ipaas:active-runtime`.
 *   - When the selected runtime is deleted (or doesn't load), falls back
 *     to the default runtime.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api';
import type { RuntimeSummary } from '../types';

const STORAGE_KEY = 'pipebase:active-runtime';

interface ActiveRuntimeState {
  runtimes: RuntimeSummary[];
  /** The selected runtime, or null while the list is loading. */
  active: RuntimeSummary | null;
  loading: boolean;
  /** True when the last read of the runtime list failed. */
  loadFailed: boolean;
  /** Pick a specific runtime by id. Persists to localStorage. */
  setActive: (id: string) => void;
  /** Re-fetch the runtime list (e.g. after creating a new one). */
  refresh: () => Promise<void>;
}

const Ctx = createContext<ActiveRuntimeState>({
  runtimes: [],
  active: null,
  loading: true,
  loadFailed: false,
  setActive: () => undefined,
  refresh: () => Promise.resolve(),
});

export function ActiveRuntimeProvider({ children }: { children: ReactNode }) {
  const [runtimes, setRuntimes] = useState<RuntimeSummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEY);
  });
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const list = await api.listRuntimes();
      setRuntimes(list);
      setLoadFailed(false);
    } catch {
      /* failure-safe; keep previous list */
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Resolve the active runtime: stored id if it's still valid, else default.
  const active: RuntimeSummary | null = (() => {
    if (runtimes.length === 0) return null;
    if (activeId) {
      const match = runtimes.find((r) => r.id === activeId);
      if (match) return match;
    }
    return runtimes.find((r) => r.isDefault) ?? runtimes[0] ?? null;
  })();

  const setActive = useCallback((id: string) => {
    setActiveId(id);
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, id);
  }, []);

  return (
    <Ctx.Provider value={{ runtimes, active, loading, loadFailed, setActive, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useActiveRuntime(): ActiveRuntimeState {
  return useContext(Ctx);
}
